import { useState, useRef, useEffect } from "react";
import styles from "./ImageGallery.scss";
import classnamesBind from "classnames/bind";
import useTouchControl from "../../hooks/useTouchControl";
import { useRuntimeConstants } from "../../context/runtimeConstants/RuntimeConstantsContext";
import {
  ImageGalleryComponentProps,
  GalleryImage,
} from "../../types/components/imageGallery";

const classnames = classnamesBind.bind(styles);

function getChildNodes(
  elements: NodeListOf<ChildNode> | ChildNode[],
): ChildNode[] {
  const arr = Array.from(elements);
  if (arr.some((element) => element.childNodes.length)) {
    return arr
      .filter((element) => element.childNodes.length)
      .flatMap((element) => [
        ...Array.from(element.childNodes),
        ...getChildNodes(element.childNodes),
      ]);
  }
  return [];
}

interface ImageIndexArgs {
  previousImageIndex: number;
  imageIndex: number;
  lastImageIndex: number;
}

function movingFirstToLastImage({
  previousImageIndex,
  imageIndex,
  lastImageIndex,
}: ImageIndexArgs): boolean {
  return previousImageIndex === 0 && imageIndex === lastImageIndex;
}

function movingLastToFirstImage({
  previousImageIndex,
  imageIndex,
  lastImageIndex,
}: ImageIndexArgs): boolean {
  return previousImageIndex === lastImageIndex && imageIndex === 0;
}

export default function ImageGallery({
  gallery,
  screenHoverable,
  mapImage,
  fullHeightContainer = true,
}: ImageGalleryComponentProps): JSX.Element | null {
  const shouldRenderSingleImage =
    gallery?.imageList.length === 1 && gallery.atIndex !== null;
  const imageListLength = gallery.imageList.length;
  const totalItems = imageListLength + 2;
  const itemWidthPercent = 100 / totalItems;

  const [imageIndex, setImageIndex] = useState(
    gallery?.atIndex ? gallery.atIndex : 0,
  );
  const [animated, setAnimated] = useState(false);
  const [touchStartPosition, setTouchStartPosition] = useState<number | null>(
    null,
  );
  const paginationButtonRefs = useRef<(HTMLElement | ChildNode)[]>([]);
  const chevronButtonRefs = useRef<(HTMLElement | ChildNode)[]>([]);
  const galleryContainerRef = useRef<HTMLDivElement>(null);
  const initialRender = useRef(true);
  const previousIndex = useRef(imageIndex);
  const lastImageIndex = imageListLength - 1;
  const { getTranslation } = useRuntimeConstants() as {
    getTranslation: (key: string, params?: Record<string, unknown>) => string;
  };

  const getGalleryPosition = (index: number): string =>
    `-${(index + 1) * 100}%`;

  useEffect(() => {
    const previousImageIndex = previousIndex.current;
    previousIndex.current = imageIndex;

    const setGalleryContainerPosition = ({
      startAtClone = true,
      index = imageIndex,
    }: { startAtClone?: boolean; index?: number } = {}) => {
      if (shouldRenderSingleImage || !galleryContainerRef.current) {
        return;
      }
      if (startAtClone) {
        galleryContainerRef.current.style.marginLeft =
          getGalleryPosition(index);
      } else {
        galleryContainerRef.current.style.marginLeft = `${-index * 100}%`;
      }

      setTimeout(() => setAnimated(true));
    };

    if (animated || initialRender.current) {
      setGalleryContainerPosition();
      return;
    }

    if (
      movingLastToFirstImage({ previousImageIndex, imageIndex, lastImageIndex })
    ) {
      setGalleryContainerPosition({ startAtClone: false, index: 0 });
      return;
    }

    if (
      movingFirstToLastImage({ previousImageIndex, imageIndex, lastImageIndex })
    ) {
      setGalleryContainerPosition({ index: lastImageIndex + 1 });
    }
  }, [animated, imageIndex, lastImageIndex, shouldRenderSingleImage]);

  useEffect(() => {
    initialRender.current = false;
  }, []);

  const setPaginationButtonRefs = (element: HTMLButtonElement | null) => {
    if (element) {
      paginationButtonRefs.current.push(element);
      paginationButtonRefs.current.push(...getChildNodes(element.childNodes));
    }
  };
  const setChevronButtonRefs = (element: HTMLButtonElement | null) => {
    if (element) {
      chevronButtonRefs.current.push(element);
      chevronButtonRefs.current.push(...getChildNodes(element.childNodes));
    }
  };

  const handleNextClick = () => {
    if (imageIndex === lastImageIndex) {
      setAnimated(false);
      setImageIndex(0);
    } else {
      setImageIndex((imageIndex) => imageIndex + 1);
    }
  };

  const handlePreviousClick = () => {
    if (imageIndex === 0) {
      setAnimated(false);
      setImageIndex(lastImageIndex);
    } else {
      setImageIndex((imageIndex) => imageIndex - 1);
    }
  };

  const paginationButtons = () => {
    paginationButtonRefs.current = [];
    chevronButtonRefs.current = [];
    return gallery.imageList.map((image: GalleryImage, index: number) => {
      const selected = imageIndex === index;
      return (
        <button
          ref={setPaginationButtonRefs}
          key={image.url}
          className={classnames(styles.paginationButtons)}
          onClick={() => setImageIndex(index)}
          aria-current={selected}
          aria-label={getTranslation("pdp_ratings_review_gallery_image_count", {
            index: index + 1,
            total: imageListLength,
          })}
        >
          <div className={classnames(styles.circleButtons, { selected })} />
        </button>
      );
    });
  };

  const displayChevrons = () => {
    paginationButtonRefs.current = [];
    chevronButtonRefs.current = [];
    return (
      <div>
        <button
          className={classnames(styles.chevron, styles.nextButton)}
          onClick={handleNextClick}
          aria-label={getTranslation("pdp_media_gallery_carousel_next")}
          ref={setChevronButtonRefs}
        >
          <div className={classnames("product-chevron-right-large-outlined")} />
        </button>
        <button
          className={classnames(styles.chevron, styles.previousButton)}
          onClick={handlePreviousClick}
          aria-label={getTranslation("pdp_media_gallery_carousel_previous")}
          ref={setChevronButtonRefs}
        >
          <div className={classnames("product-chevron-left-large-outlined")} />
        </button>
      </div>
    );
  };

  const touchStartHandler = (event: TouchEvent) =>
    setTouchStartPosition(
      event.touches.length === 1 ? event.changedTouches[0].clientX : null,
    );

  const shouldFireTouchEvent = (event: TouchEvent) => {
    const clickableElements = [
      ...paginationButtonRefs.current,
      ...chevronButtonRefs.current,
    ];

    return (
      !clickableElements.includes(event.target as ChildNode) &&
      touchStartPosition !== null
    );
  };

  const touchEndHandler = (event: TouchEvent) => {
    if (shouldFireTouchEvent(event)) {
      const endTouchPosition = event.changedTouches[0].clientX;
      const direction = endTouchPosition - touchStartPosition!;

      direction <= 0 ? handleNextClick() : handlePreviousClick();
    }

    setTouchStartPosition(null);
  };

  useTouchControl({
    startHandler: touchStartHandler,
    endHandler: touchEndHandler,
  });

  const renderGallery = () => {
    const itemStyle = { width: `${itemWidthPercent}%` };

    return (
      <div
        ref={galleryContainerRef}
        className={classnames(styles.galleryContainer, {
          animated,
          fullHeightContainer,
        })}
        style={{
          width: `${totalItems * 100}%`,
          marginLeft: getGalleryPosition(imageIndex),
        }}
      >
        <div
          className={styles.galleryItem}
          style={itemStyle}
          aria-hidden="true"
        >
          {mapImage({
            image: gallery.imageList[imageListLength - 1],
            index: imageListLength - 1,
            imageIndex,
          })}
        </div>
        {gallery.imageList.map((image: GalleryImage, index: number) => (
          <div className={styles.galleryItem} key={image.url} style={itemStyle}>
            {mapImage({ image, index, imageIndex })}
          </div>
        ))}
        <div
          className={styles.galleryItem}
          style={itemStyle}
          aria-hidden="true"
        >
          {mapImage({ image: gallery.imageList[0], index: 0, imageIndex })}
        </div>
        {screenHoverable ? (
          displayChevrons()
        ) : (
          <div className={classnames(styles.paginationButtonsContainer)}>
            {paginationButtons()}
          </div>
        )}
      </div>
    );
  };

  return gallery?.imageList.length ? (
    shouldRenderSingleImage ? (
      <div
        className={classnames(styles.imageContainer, {
          fullHeightContainer,
        })}
      >
        {mapImage({ image: gallery.imageList[0], index: 0, imageIndex })}
      </div>
    ) : (
      renderGallery()
    )
  ) : null;
}
